import type { SsaStatus, SsaSubStatus } from '../../api/types';

// Pure attendance rules. No runtime imports, so node --test can load this directly.

export interface AttendanceDraft {
  status: SsaStatus;
  subStatus: SsaSubStatus;
}

/** Structural minimum of QltSlotAttendance that these rules read. */
export interface AttendanceRow {
  _id: string;
  status: SsaStatus;
  subStatus: SsaSubStatus;
  // the server's carry-forward cascade filters on this; omit it and the cascade silently no-ops
  student?: { _id?: string | number; id?: string | number } | string | number | null;
}

export interface AttendanceCounts {
  total: number;
  present: number;
  absent: number;
  late: number;
  unmarked: number;
  onTime: number;
  excused: number;
  unexcused: number;
}

export interface AttendanceChange extends AttendanceDraft {
  id: string;
  student?: string;
}

export function studentIdOf(row: AttendanceRow): string {
  const value = row.student;
  if (!value) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  return String(value._id || value.id || '');
}

export const PRESENT_ON_TIME: AttendanceDraft = { status: 'present', subStatus: 'onTime' };

export const EMPTY_COUNTS: AttendanceCounts = { total: 0, present: 0, absent: 0, late: 0, unmarked: 0, onTime: 0, excused: 0, unexcused: 0 };

// PO rule: an unrecorded row reads as present and on time, so the teacher only ticks the exceptions.
export function defaultDraft(rows: AttendanceRow[]): Record<string, AttendanceDraft> {
  const draft: Record<string, AttendanceDraft> = {};
  for (let i = 0; i < rows.length; i += 1) {
    // a row the school already recorded keeps its own value and is never defaulted over
    if (rows[i].status === 'none') draft[rows[i]._id] = { status: 'present', subStatus: 'onTime' };
  }
  return draft;
}

// Group A picks group B: present takes đúng giờ/đi muộn, absent takes có phép/không phép.
export function normalizeDraft(value: AttendanceDraft): AttendanceDraft {
  if (value.status === 'present') {
    return value.subStatus === 'lateTime' ? { status: 'present', subStatus: 'lateTime' } : { status: 'present', subStatus: 'onTime' };
  }
  if (value.status === 'absent') {
    return value.subStatus === 'unexcused' ? { status: 'absent', subStatus: 'unexcused' } : { status: 'absent', subStatus: 'excused' };
  }
  return { status: 'none', subStatus: 'none' };
}

export function deriveCounts(rows: AttendanceRow[], draft: Record<string, AttendanceDraft>): AttendanceCounts {
  const counts: AttendanceCounts = { ...EMPTY_COUNTS, total: rows.length };
  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i];
    const value = draft[row._id] || row;
    const late = value.status === 'present' && value.subStatus === 'lateTime';
    // the buckets partition the roster, so đi muộn is counted outside có mặt, never inside it
    if (late) counts.late += 1;
    else if (value.status === 'present') counts.present += 1;
    else if (value.status === 'absent') counts.absent += 1;
    else counts.unmarked += 1;
    if (value.status === 'present' && value.subStatus === 'onTime') counts.onTime += 1;
    if (value.status === 'absent' && value.subStatus === 'excused') counts.excused += 1;
    if (value.status === 'absent' && value.subStatus === 'unexcused') counts.unexcused += 1;
  }
  return counts;
}

// Rows whose draft differs from what the server holds. A defaulted roster is a real diff, never a no-op.
export function pendingChanges(rowsById: Record<string, AttendanceRow>, draft: Record<string, AttendanceDraft>): AttendanceChange[] {
  const changes: AttendanceChange[] = [];
  const ids = Object.keys(draft);
  for (let i = 0; i < ids.length; i += 1) {
    const id = ids[i];
    const row = rowsById[id];
    const value = draft[id];
    if (!row) continue;
    if (row.status === value.status && row.subStatus === value.subStatus) continue;
    const student = studentIdOf(row);
    changes.push(student ? { id, student, status: value.status, subStatus: value.subStatus }
                         : { id, status: value.status, subStatus: value.subStatus });
  }
  return changes;
}

/** The identity tuple of one period's roster (class, indexDay, date). */
export interface AttendanceContextKey {
  class: string | number;
  indexDay: string;
  date: string;
}

/** A save's answer may only touch the store while the store still shows the period it was sent for. */
export function sameAttendanceContext(a: AttendanceContextKey | null, b: AttendanceContextKey | null): boolean {
  if (!a || !b) return false;
  return String(a.class) === String(b.class) && a.indexDay === b.indexDay && a.date === b.date;
}
